import {
  Card,
  Skeleton,
  Tile,
  TileContent,
  TileMedia,
  TileText,
  TileTitle,
} from '@design-system';
import Link from 'next/link';
import { ReactNode } from 'react';

import { Image } from '../Image';

export const ArticleItemSkeleton = ({
  variant,
}: {
  variant: 'card' | 'list';
}) => {
  if (variant === 'card') {
    return (
      <Card className="flex flex-col">
        <Skeleton className="aspect-[1/1]" />
        <TileContent direction="column" className="w-full gap-1 p-4">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-5 w-1/2" />
        </TileContent>
      </Card>
    );
  }

  return (
    <Tile className="py-4" direction="row">
      <Skeleton className="size-16 shrink-0" />
      <TileContent direction="column" className="w-full gap-1">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-5 w-1/2" />
      </TileContent>
    </Tile>
  );
};

export const ArticleItem = ({
  description,
  href,
  image,
  onClick,
  suffix,
  text,
  title,
  variant,
}: {
  variant: 'card' | 'list';
  href: string;
  title: string;
  description: string;
  text?: string;
  image: {
    filename: string;
    focus?: string;
    alt: string;
    ratio: '1/1' | '16/9' | '3/4' | '4/3';
  };
  suffix?: ReactNode;
  onClick: () => void;
}) => {
  if (variant === 'card') {
    return (
      <Card>
        <Tile className="group gap-4" direction="column">
          <TileMedia className="rounded-none" overlay={suffix}>
            <Image
              alt={image.alt}
              src={image.filename}
              focus={image.focus}
              hoverEffect
              size={{
                sm: { ratio: image.ratio, span: 12 },
                md: { ratio: image.ratio, span: 6 },
                lg: { ratio: image.ratio, span: 4 },
              }}
            />
          </TileMedia>

          <TileContent className="w-full px-4 pb-4" direction="column">
            <TileTitle>
              <Link
                href={href}
                onClick={onClick}
                prefetch={false}
                className="after:absolute after:inset-0 after:block after:content-['']"
              >
                {title}
              </Link>
            </TileTitle>
            <TileText>{description}</TileText>

            {text ? (
              <TileText className="mt-3 line-clamp-3" variant="muted">
                {text}
              </TileText>
            ) : null}
          </TileContent>
        </Tile>
      </Card>
    );
  }

  return (
    <Tile className="group py-4" direction="row">
      <TileMedia className="size-16 shrink-0">
        <Image
          src={image.filename}
          focus={image.focus}
          alt={image.alt}
          hoverEffect
          width={128}
        />
      </TileMedia>

      <TileContent className="w-full" direction="column">
        <TileTitle>
          <Link
            href={href}
            onClick={onClick}
            prefetch={false}
            className="after:absolute after:inset-0 after:block after:content-['']"
          >
            {title}
          </Link>
        </TileTitle>
        <TileText>{description}</TileText>
      </TileContent>

      <TileContent className="shrink-0" direction="row">
        {suffix}
      </TileContent>
    </Tile>
  );
};
